import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  DeliveryDestination,
  BriefingSubscription,
  BriefOfficialRecipient,
  OfficialBriefingHistoryEntry,
} from '@tsr/shared-types';
import {
  fetchDeliveryDestinations,
  listBriefingSubscriptions,
  briefOfficial,
  fetchOfficialBriefings,
  recordOfficialResponse,
} from './api';
import { getSessionId } from './lib/operatorActions';
import {
  BRIEF_OFFICIAL_HEADING,
  BRIEF_OFFICIAL_CAPTION,
  EMAIL_SITREP_TO_OFFICIAL_LABEL,
  EMAIL_SITREP_BUSY_LABEL,
  RECORD_PHONE_CONTACT_LABEL,
  RECORD_PHONE_CONTACT_BUSY_LABEL,
  BRIEF_OFFICIAL_ADHOC_EMAIL_PLACEHOLDER,
  BRIEF_OFFICIAL_ADHOC_ADD_LABEL,
  BRIEF_OFFICIAL_NO_CONTACTS_EMPTY,
  REACHED_BY_PHONE_TIME_LABEL,
  REACHED_BY_PHONE_NAME_LABEL,
  REACHED_BY_PHONE_NOTE_LABEL,
  BRIEF_OFFICIAL_SENT_NOT_ACK_NOTE,
  sitrepEmailedLabel,
  phoneContactRecordedLabel,
  RECORD_OFFICIAL_RESPONSE_LABEL,
  RECORD_OFFICIAL_RESPONSE_BUSY_LABEL,
  OFFICIAL_BRIEFING_HISTORY_HEADING,
  OFFICIAL_BRIEFING_HISTORY_EMPTY,
  RECORD_RESPONSE_CHANNEL_LABEL,
  RECORD_RESPONSE_CHANNEL_EMAIL,
  RECORD_RESPONSE_CHANNEL_PHONE,
  RECORD_RESPONSE_OFFICIAL_LABEL,
  RECORD_RESPONSE_TIME_LABEL,
  RECORD_RESPONSE_NOTE_LABEL,
  responseRecordedLabel,
  responseReceivedBySeatLabel,
} from './incidentVocabulary';

/**
 * #328 Phase 1 — OUT-OF-APP OFFICIAL BRIEFING box (operator view). Briefs a
 * decision-maker who sits OUTSIDE the ops centre (a mayor / head of council / ECG —
 * Ontario IMS §7.4 / App A #19 "briefing up"; App A #21) with this SITREP, IN-COP, via either channel:
 *   • Email the Situation Report PDF to operator-selected officials (registry picks
 *     + briefing subscribers + ad-hoc typed addresses).
 *   • Record that an official was reached by phone (mirrors the emergency-declaration
 *     call register) — surfacing the "who to call" number from the registry.
 * Both persist an in-COP record (App A #72). Register is INFORM/BRIEF: never commands
 * the recipient. HONESTY: a send shows "Sent HH:MM", never a read/acknowledged flag.
 */

const HHMM_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

/** A briefable official: an email address, an optional phone, and a display label. */
interface Contact {
  key: string;
  label: string;
  email: string | null;
  phone: string | null;
}

function contactsFrom(
  destinations: DeliveryDestination[],
  subs: BriefingSubscription[],
): Contact[] {
  const out: Contact[] = [];
  const seen = new Set<string>();
  // Registry destinations first (they carry the "who to call" phone). Only rows with an
  // email address or a phone are briefable here.
  for (const d of destinations) {
    const email = d.channel === 'email' && d.address ? d.address : null;
    const phone = d.contact_phone ?? null;
    if (!email && !phone) continue;
    const key = `dest:${d.id}`;
    seen.add(email ?? key);
    out.push({ key, label: d.name, email, phone });
  }
  // Per-incident briefing subscribers (email only) — de-duped against registry emails.
  for (const s of subs) {
    if (seen.has(s.email_address)) continue;
    seen.add(s.email_address);
    out.push({
      key: `sub:${s.id}`,
      label: `${s.email_address} · ${s.role}`,
      email: s.email_address,
      phone: null,
    });
  }
  return out;
}

export interface OfficialBriefingBoxProps {
  /** The persisted SITREP to brief with. */
  sitrepId: string;
  /** Incident in scope (enables subscriber contacts); null → registry only. */
  incidentId: string | null;
}

export function OfficialBriefingBox({
  sitrepId,
  incidentId,
}: OfficialBriefingBoxProps): JSX.Element {
  const [destinations, setDestinations] = useState<DeliveryDestination[]>([]);
  const [subs, setSubs] = useState<BriefingSubscription[]>([]);

  // Email state.
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [adhoc, setAdhoc] = useState('');
  const [adhocList, setAdhocList] = useState<string[]>([]);
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailResult, setEmailResult] = useState<{ hhmm: string; to: string[] } | null>(null);
  const [emailErr, setEmailErr] = useState<string | null>(null);

  // Phone state.
  const [phoneName, setPhoneName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [reachedAt, setReachedAt] = useState('');
  const [phoneNote, setPhoneNote] = useState('');
  const [phoneBusy, setPhoneBusy] = useState(false);
  const [phoneResult, setPhoneResult] = useState<string | null>(null);
  const [phoneErr, setPhoneErr] = useState<string | null>(null);

  // #568 — the persisted briefing history (sent briefs + recorded responses) and the
  // record-response affordance. Results were previously TRANSIENT component state; this reads
  // them back so a reload keeps the record (App A #72 record-keeping, retrievable for AAR).
  const [history, setHistory] = useState<OfficialBriefingHistoryEntry[]>([]);
  const [respChannel, setRespChannel] = useState<'email' | 'phone'>('email');
  const [respOfficial, setRespOfficial] = useState('');
  const [respTime, setRespTime] = useState('');
  const [respNote, setRespNote] = useState('');
  const [respBusy, setRespBusy] = useState(false);
  const [respResult, setRespResult] = useState<string | null>(null);
  const [respErr, setRespErr] = useState<string | null>(null);

  // Whether the SITREP has been briefed at least once — a response attaches to a briefing that
  // happened (the record-response route 404s otherwise), so the affordance only shows once a
  // brief exists in the history.
  const hasBrief = useMemo(() => history.some((h) => h.kind === 'brief_official'), [history]);

  const refresh = useCallback(
    async (signal?: AbortSignal) => {
      try {
        setDestinations(await fetchDeliveryDestinations(signal));
      } catch {
        /* registry unreachable — ad-hoc entry still works */
      }
      if (incidentId) {
        try {
          setSubs(await listBriefingSubscriptions(incidentId, signal));
        } catch {
          /* non-fatal */
        }
      } else {
        setSubs([]);
      }
    },
    [incidentId],
  );

  const refreshHistory = useCallback(
    async (signal?: AbortSignal) => {
      try {
        const res = await fetchOfficialBriefings(sitrepId, signal);
        setHistory(res.entries);
      } catch {
        /* history unreachable — the send/record affordances still work */
      }
    },
    [sitrepId],
  );

  useEffect(() => {
    const ctl = new AbortController();
    void refresh(ctl.signal);
    void refreshHistory(ctl.signal);
    return () => ctl.abort();
  }, [refresh, refreshHistory]);

  const contacts = useMemo(() => contactsFrom(destinations, subs), [destinations, subs]);

  const selectedEmails = useMemo<BriefOfficialRecipient[]>(() => {
    const picked: BriefOfficialRecipient[] = [];
    for (const c of contacts) {
      if (checked[c.key] && c.email) picked.push({ email: c.email, label: c.label });
    }
    for (const e of adhocList) picked.push({ email: e });
    return picked;
  }, [contacts, checked, adhocList]);

  function addAdhoc(): void {
    const e = adhoc.trim();
    // A light client-side sanity check; the server Zod-validates authoritatively.
    if (!e || !e.includes('@') || adhocList.includes(e)) return;
    setAdhocList((prev) => [...prev, e]);
    setAdhoc('');
  }

  async function onEmail(): Promise<void> {
    if (selectedEmails.length === 0) return;
    setEmailBusy(true);
    setEmailErr(null);
    setEmailResult(null);
    try {
      const res = await briefOfficial(sitrepId, {
        channel: 'email',
        session_id: getSessionId(),
        recipients: selectedEmails,
      });
      const hhmm = res.sent_at
        ? new Date(res.sent_at).toLocaleTimeString('en-CA', { hour: '2-digit', minute: '2-digit' })
        : '';
      setEmailResult({ hhmm, to: selectedEmails.map((r) => r.label ?? r.email) });
      setChecked({});
      setAdhocList([]);
      void refreshHistory();
    } catch {
      setEmailErr('Could not send the Situation Report. Please retry.');
    } finally {
      setEmailBusy(false);
    }
  }

  async function onPhone(): Promise<void> {
    if (!phoneName.trim() || !HHMM_RE.test(reachedAt)) return;
    setPhoneBusy(true);
    setPhoneErr(null);
    setPhoneResult(null);
    try {
      await briefOfficial(sitrepId, {
        channel: 'phone',
        session_id: getSessionId(),
        recipient_name: phoneName.trim(),
        recipient_phone: phoneNumber.trim() || null,
        reached_at_local: reachedAt,
        note: phoneNote.trim() || undefined,
      });
      setPhoneResult(phoneContactRecordedLabel(reachedAt));
      setPhoneNote('');
      void refreshHistory();
    } catch {
      setPhoneErr('Could not record the phone contact. Please retry.');
    } finally {
      setPhoneBusy(false);
    }
  }

  async function onRecordResponse(): Promise<void> {
    if (!respOfficial.trim() || !HHMM_RE.test(respTime)) return;
    setRespBusy(true);
    setRespErr(null);
    setRespResult(null);
    try {
      await recordOfficialResponse(sitrepId, {
        session_id: getSessionId(),
        channel: respChannel,
        official: respOfficial.trim(),
        received_at_local: respTime,
        note: respNote.trim() || undefined,
      });
      setRespResult(responseRecordedLabel(respTime));
      setRespOfficial('');
      setRespTime('');
      setRespNote('');
      void refreshHistory();
    } catch {
      setRespErr('Could not record the response. Please retry.');
    } finally {
      setRespBusy(false);
    }
  }

  return (
    <section
      className="mx-10 mb-8 rounded-xl border border-slate-700 bg-slate-900/60 p-6"
      data-testid="official-briefing-box"
    >
      <h3 className="text-lg font-semibold text-slate-100">{BRIEF_OFFICIAL_HEADING}</h3>
      <p className="mt-1 max-w-3xl text-sm text-slate-400">{BRIEF_OFFICIAL_CAPTION}</p>

      <div className="mt-5 grid gap-6 lg:grid-cols-2">
        {/* ── Email the Situation Report ─────────────────────────────── */}
        <div>
          <h4 className="mb-2 text-sm font-semibold uppercase tracking-wide text-slate-300">
            {EMAIL_SITREP_TO_OFFICIAL_LABEL}
          </h4>
          {contacts.length === 0 ? (
            <p className="mb-3 text-sm text-slate-400">{BRIEF_OFFICIAL_NO_CONTACTS_EMPTY}</p>
          ) : (
            <ul className="mb-3 space-y-1.5" data-testid="official-contacts">
              {contacts.map((c) => (
                <li key={c.key} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    disabled={!c.email}
                    checked={!!checked[c.key]}
                    onChange={(e) =>
                      setChecked((prev) => ({ ...prev, [c.key]: e.target.checked }))
                    }
                    data-testid={`contact-${c.key}`}
                  />
                  <span className="text-slate-100">{c.label}</span>
                  {c.email && <span className="text-slate-500">· {c.email}</span>}
                  {c.phone && <span className="text-slate-500">· ☎ {c.phone}</span>}
                </li>
              ))}
            </ul>
          )}

          {/* Ad-hoc typed recipient (no pre-registration). */}
          <div className="mb-3 flex flex-wrap gap-2">
            <input
              type="email"
              placeholder={BRIEF_OFFICIAL_ADHOC_EMAIL_PLACEHOLDER}
              value={adhoc}
              onChange={(e) => setAdhoc(e.target.value)}
              className="min-w-0 flex-1 rounded bg-slate-800 px-2 py-1.5 text-sm text-slate-100"
              data-testid="adhoc-email"
            />
            <button
              type="button"
              onClick={addAdhoc}
              className="rounded-md bg-slate-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-600"
              data-testid="adhoc-add"
            >
              {BRIEF_OFFICIAL_ADHOC_ADD_LABEL}
            </button>
          </div>
          {adhocList.length > 0 && (
            <ul className="mb-3 flex flex-wrap gap-2" data-testid="adhoc-list">
              {adhocList.map((e) => (
                <li
                  key={e}
                  className="rounded bg-slate-800 px-2 py-1 text-xs text-slate-200"
                >
                  {e}
                  <button
                    type="button"
                    onClick={() => setAdhocList((prev) => prev.filter((x) => x !== e))}
                    className="ml-1.5 text-rose-400 hover:text-rose-300"
                    aria-label={`Remove ${e}`}
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          )}

          <button
            type="button"
            disabled={emailBusy || selectedEmails.length === 0}
            onClick={() => void onEmail()}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-500 disabled:opacity-50"
            data-testid="email-sitrep"
          >
            {emailBusy ? EMAIL_SITREP_BUSY_LABEL : EMAIL_SITREP_TO_OFFICIAL_LABEL}
          </button>

          {emailErr && <p className="mt-2 text-sm text-rose-400">{emailErr}</p>}
          {emailResult && (
            <div className="mt-3 text-sm" data-testid="email-result">
              {/* HONESTY: SENT, not acknowledged. */}
              <p className="text-emerald-400">{sitrepEmailedLabel(emailResult.hhmm)}</p>
              <p className="text-slate-400">
                To {emailResult.to.join(', ')}. {BRIEF_OFFICIAL_SENT_NOT_ACK_NOTE}
              </p>
            </div>
          )}
        </div>

        {/* ── Record: reached by phone ───────────────────────────────── */}
        <div>
          <h4 className="mb-2 text-sm font-semibold uppercase tracking-wide text-slate-300">
            {RECORD_PHONE_CONTACT_LABEL}
          </h4>
          <div className="space-y-2">
            <label className="block text-xs text-slate-400">
              {REACHED_BY_PHONE_NAME_LABEL}
              <input
                type="text"
                value={phoneName}
                onChange={(e) => setPhoneName(e.target.value)}
                className="mt-1 w-full rounded bg-slate-800 px-2 py-1.5 text-sm text-slate-100"
                data-testid="phone-name"
              />
            </label>
            <div className="flex gap-2">
              <label className="flex-1 text-xs text-slate-400">
                Phone
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  className="mt-1 w-full rounded bg-slate-800 px-2 py-1.5 text-sm text-slate-100"
                  data-testid="phone-number"
                />
              </label>
              <label className="w-28 text-xs text-slate-400">
                {REACHED_BY_PHONE_TIME_LABEL}
                <input
                  type="text"
                  placeholder="14:32"
                  value={reachedAt}
                  onChange={(e) => setReachedAt(e.target.value)}
                  className="mt-1 w-full rounded bg-slate-800 px-2 py-1.5 text-sm text-slate-100"
                  data-testid="phone-time"
                />
              </label>
            </div>
            <label className="block text-xs text-slate-400">
              {REACHED_BY_PHONE_NOTE_LABEL}
              <textarea
                value={phoneNote}
                onChange={(e) => setPhoneNote(e.target.value)}
                rows={2}
                className="mt-1 w-full rounded bg-slate-800 px-2 py-1.5 text-sm text-slate-100"
                data-testid="phone-note"
              />
            </label>
          </div>
          <button
            type="button"
            disabled={phoneBusy || !phoneName.trim() || !HHMM_RE.test(reachedAt)}
            onClick={() => void onPhone()}
            className="mt-3 rounded-md bg-slate-700 px-4 py-2 text-sm font-medium text-white hover:bg-slate-600 disabled:opacity-50"
            data-testid="record-phone"
          >
            {phoneBusy ? RECORD_PHONE_CONTACT_BUSY_LABEL : RECORD_PHONE_CONTACT_LABEL}
          </button>
          {phoneErr && <p className="mt-2 text-sm text-rose-400">{phoneErr}</p>}
          {phoneResult && (
            <p className="mt-3 text-sm text-emerald-400" data-testid="phone-result">
              {phoneResult}
            </p>
          )}
        </div>
      </div>

      {/* ── #568 Briefing record: the persisted history (sent briefs + recorded responses),
          attributed to the recording seat + timestamped (App A #72 record-keeping, retrievable
          for after-action review) + the "Record response" affordance. ── */}
      <div className="mt-8 border-t border-slate-800 pt-6">
        <h4 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-300">
          {OFFICIAL_BRIEFING_HISTORY_HEADING}
        </h4>

        {history.length === 0 ? (
          <p className="text-sm text-slate-400" data-testid="briefing-history-empty">
            {OFFICIAL_BRIEFING_HISTORY_EMPTY}
          </p>
        ) : (
          <ul className="space-y-2" data-testid="official-briefing-history">
            {history.map((h) => {
              const recordedHHMM = new Date(h.recorded_at).toLocaleTimeString('en-CA', {
                hour: '2-digit',
                minute: '2-digit',
              });
              const isResponse = h.kind === 'record_official_response';
              const primary = isResponse
                ? responseRecordedLabel(h.attested_local_time ?? recordedHHMM)
                : h.channel === 'phone'
                  ? phoneContactRecordedLabel(h.attested_local_time ?? recordedHHMM)
                  : sitrepEmailedLabel(recordedHHMM);
              return (
                <li
                  key={h.id}
                  className="rounded-lg border border-slate-800 bg-slate-900/60 px-4 py-3 text-sm"
                  data-testid="briefing-history-entry"
                >
                  <p
                    className={isResponse ? 'font-medium text-sky-300' : 'font-medium text-slate-100'}
                  >
                    {primary}
                  </p>
                  {h.counterpart && (
                    <p className="text-slate-400">
                      {isResponse ? 'From' : 'To'} {h.counterpart}
                    </p>
                  )}
                  {h.note && <p className="mt-1 text-slate-300">{h.note}</p>}
                  {/* Attribution to the recording seat (the app's existing seat-label mechanism). */}
                  <p className="mt-1 text-xs text-slate-500" data-testid="history-entry-seat">
                    {isResponse
                      ? responseReceivedBySeatLabel(h.recorded_by_seat)
                      : h.recorded_by_seat}
                  </p>
                </li>
              );
            })}
          </ul>
        )}

        {/* Record-response affordance — only once the SITREP has been briefed (a response
            attaches to a briefing that happened). */}
        {hasBrief && (
          <div className="mt-5 max-w-2xl" data-testid="record-response-form">
            <h5 className="mb-2 text-sm font-semibold text-slate-200">
              {RECORD_OFFICIAL_RESPONSE_LABEL}
            </h5>
            <div className="space-y-2">
              <div className="flex items-center gap-4 text-sm text-slate-300">
                <span className="text-xs text-slate-400">{RECORD_RESPONSE_CHANNEL_LABEL}</span>
                <label className="flex items-center gap-1.5">
                  <input
                    type="radio"
                    name="resp-channel"
                    checked={respChannel === 'email'}
                    onChange={() => setRespChannel('email')}
                    data-testid="record-response-channel-email"
                  />
                  {RECORD_RESPONSE_CHANNEL_EMAIL}
                </label>
                <label className="flex items-center gap-1.5">
                  <input
                    type="radio"
                    name="resp-channel"
                    checked={respChannel === 'phone'}
                    onChange={() => setRespChannel('phone')}
                    data-testid="record-response-channel-phone"
                  />
                  {RECORD_RESPONSE_CHANNEL_PHONE}
                </label>
              </div>
              <div className="flex gap-2">
                <label className="flex-1 text-xs text-slate-400">
                  {RECORD_RESPONSE_OFFICIAL_LABEL}
                  <input
                    type="text"
                    value={respOfficial}
                    onChange={(e) => setRespOfficial(e.target.value)}
                    className="mt-1 w-full rounded bg-slate-800 px-2 py-1.5 text-sm text-slate-100"
                    data-testid="record-response-official"
                  />
                </label>
                <label className="w-32 text-xs text-slate-400">
                  {RECORD_RESPONSE_TIME_LABEL}
                  <input
                    type="text"
                    placeholder="15:10"
                    value={respTime}
                    onChange={(e) => setRespTime(e.target.value)}
                    className="mt-1 w-full rounded bg-slate-800 px-2 py-1.5 text-sm text-slate-100"
                    data-testid="record-response-time"
                  />
                </label>
              </div>
              <label className="block text-xs text-slate-400">
                {RECORD_RESPONSE_NOTE_LABEL}
                <textarea
                  value={respNote}
                  onChange={(e) => setRespNote(e.target.value)}
                  rows={2}
                  className="mt-1 w-full rounded bg-slate-800 px-2 py-1.5 text-sm text-slate-100"
                  data-testid="record-response-note"
                />
              </label>
            </div>
            <button
              type="button"
              disabled={respBusy || !respOfficial.trim() || !HHMM_RE.test(respTime)}
              onClick={() => void onRecordResponse()}
              className="mt-3 rounded-md bg-sky-700 px-4 py-2 text-sm font-medium text-white hover:bg-sky-600 disabled:opacity-50"
              data-testid="record-response-submit"
            >
              {respBusy ? RECORD_OFFICIAL_RESPONSE_BUSY_LABEL : RECORD_OFFICIAL_RESPONSE_LABEL}
            </button>
            {respErr && <p className="mt-2 text-sm text-rose-400">{respErr}</p>}
            {respResult && (
              <p className="mt-3 text-sm text-sky-300" data-testid="record-response-result">
                {respResult}
              </p>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
